import React from 'react'
import { useNormalizedPublishedTime } from '@libs/getNormalizedPublishedTime'
import { TagArticles_tagArticles_articles } from '@apollo-types/TagArticles'
import { appendUrlSearchParams } from '@libs/url-utils'
import styled from 'styled-components'
import tw, { theme } from 'twin.macro'
import PopLink from '@components/PopLink'
import { tagUrl } from '@libs/appUrl'
import { useAmp } from 'next/amp'
import { useRNApp } from '@libs/RNApp'

interface ArticleCardProps {
  article?: TagArticles_tagArticles_articles
}

export default function ArticleCard({ article }: ArticleCardProps) {
  const { url, publisher, publishedAt, coverImage, title, id, plainTextContent, tags } = article
  const articleImageUrl = coverImage?.croppedUrl || '/images/articleCardImagePlaceholder.svg'
  const publishedTime = useNormalizedPublishedTime(publishedAt)
  const text = title || plainTextContent.slice(0, 30)
  const articleUrl = appendUrlSearchParams(url, {
    fromPage: { pageName: 'web-tag', pageComponent: 'result-list' },
  })

  const { isApp } = useRNApp()
  const isAmp = useAmp()
  const Img = isAmp ? 'amp-img' : 'img'

  return (
    <div id={`article-${id}`}>
      <Container>
        <ArticleImageContainer>
          <PopLink url={articleUrl} shouldOpenInCurrentTab={true}>
            {!isAmp ? (
              <ArticleImage>
                <img src={articleImageUrl} />
              </ArticleImage>
            ) : (
              <amp-img src={articleImageUrl} width="100" height="52" layout="responsive" />
            )}
          </PopLink>
        </ArticleImageContainer>
        <PublishInfo>
          <PopLink url={`/user/${publisher?.mid}`} shouldOpenInCurrentTab={true}>
            <div className="publisher">
              <div className="avatar">
                {publisher?.photoUrl && <Img src={publisher.photoUrl} width="24" height="24" />}
              </div>
              <p className="name">{publisher?.name}</p>
            </div>
            <p className="time" data-datetime={publishedTime.dateTime}>
              {publishedTime.formatted}
            </p>
          </PopLink>
        </PublishInfo>
        <Tags>
          {tags?.slice(0, 3).map((tag: string, index: number) => {
            return (
              <a key={index} href={tagUrl(tag)} target={ isApp ? "_blank" : '' }>
                {tag}
              </a>
            )
          })}
        </Tags>
        <PopLink url={articleUrl} shouldOpenInCurrentTab={true}>
          <Text>{text}</Text>
        </PopLink>
      </Container>
    </div>
  )
}

const Container = styled.div`
  ${tw`w-full`}
  @media (min-width: ${theme`screens.md`}) {
    ${tw`block flex-shrink-0 cursor-pointer transition duration-500`}
    ${tw`hover:opacity-80`}
    margin-bottom: 28px;
    width: 368px;
  }
`

const PublishInfo = styled.div`
  ${tw`mb-2`}
  height: 24px;
  a {
    ${tw`flex justify-between`}
  }
  img {
    ${tw`rounded-full`}
    width: 24px;
    height: 24px;
  }
  .publisher {
    ${tw`flex`}
    .avatar {
      ${tw`mr-2`}
    }
    .name {
      ${tw`text-black text-b2-d font-medium`}
      line-height: 24px;
    }
  }
  .time {
    ${tw`text-gray-600 text-c2-d`}
    line-height: 24px;
  }
`

const ArticleImageContainer = styled.div`
  ${tw`mb-2`}
  img {
    ${tw`rounded-3 object-cover`}
  }
`

const ArticleImage = styled.div`
  position: relative;
  /* 0.52: 圖片 高/寬 比例 */
  padding-top: 52%;
  img {
    ${tw`w-full h-full object-cover absolute top-0 bottom-0 right-0 left-0`}
  }
`

const Tags = styled.div`
  ${tw`text-primary-indigo mb-2`}
  max-height: 20px;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  a {
    &:not(:last-child)::after {
      ${tw`mx-2`}
      content: '∙';
    }
    ${tw`text-h6-d`}
  }
`

const Text = styled.h2`
  ${tw`text-gray-800 font-semibold mb-5`}
  font-size: 18px;
  max-height: 82px;
  /* the workaround for text-overflow: ellipsis; only works in 1 line text */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  @media (min-width: ${theme`screens.md`}) {
    font-size: 20px;
    max-height: 84px;
  }
`
